import React, { useEffect, useRef, useState } from 'react'
import queryString from 'query-string'
import { useSetState } from 'react-use'
import { useHistory, useLocation } from 'react-router-dom'
import { findFormErrors } from 'components/Forms/ExtendedFormik'
import { uniq } from 'lodash'

import Collapse from 'rc-collapse'
import { PropTypes } from 'prop-types'

// Define constants for sections, default active sections, and class name
const SECTIONS = Object.freeze(['primary', 'identification', 'ownInfo', 'service', 'reviewInfo'])
const DEFAULT_ACTIVE_SECTIONS = [SECTIONS[0]]
const CLASS_NAME = 'accordion--edit-user-sections'

/**
 * A custom hook to determine which sections of a form have validation errors.
 *
 * @param {number} formSubmitCount - The number of times the form has been submitted.
 * @param {boolean} formIsValid - Indicates if the form is valid or not.
 * @returns {Array<string>} - An array of section names with validation errors.
 */
const useSectionsWithFormErrors = (formSubmitCount, formIsValid) => {
    const [sectionsWithErrors, setSectionsWithErrors] = useState(null)

    useEffect(() => {
        // If the form is valid or no form submissions yet, exit early
        if (formIsValid || formSubmitCount <= 0) {
            return
        }

        // Get the sections element and find errors in each section
        const sectionsElement = document.querySelector(`.${CLASS_NAME}`)
        const sectionsWithErrors = SECTIONS.reduce((acc, section) => {
            const result = findFormErrors(sectionsElement.querySelector(`#section-${section}`).closest('.rc-collapse-item'))
            if (result.dataMissing) {
                acc.push(section)
            }
            return acc
        }, [])

        setSectionsWithErrors(sectionsWithErrors)
    }, [formSubmitCount, formIsValid])

    return sectionsWithErrors
}

/**
 * A component that renders collapsible sections for editing user details.
 *
 * @param {object} props - The props for the component.
 * @param {ReactNode} props.children - The content to be rendered inside the collapsible sections.
 * @param {boolean} props.expandAll - If true, all sections will be expanded.
 * @param {number} props.formSubmitCount - The number of times the form has been submitted.
 * @param {boolean} props.formIsValid - Indicates if the form is valid or not.
 * @param {Array<string>} [props.initOpenSections] - Sections to open initially.
 * @param {Array<string>} [props.initOpenErrorSections] - Sections to open if there are errors.
 * @param {object} props.errors - An object containing form errors.
 * @returns {ReactElement|null} - The rendered component or null if not ready.
 */
const EditUserCollapse = ({ children, expandAll, formSubmitCount, formIsValid, initOpenSections, initOpenErrorSections, errors }) => {
    // Determine which sections to expand based on form errors
    const expandErrors = useSectionsWithFormErrors(formSubmitCount, formIsValid)
    const history = useHistory()
    const location = useLocation()

    // Ref to track if the component has initialized
    const readyRef = useRef(false)

    // State for managing query parameters and active panels
    const [state, setState] = useSetState({
        query: queryString.parse(location.hash),
        activePanels: null,
    })

    const { query } = state

    useEffect(() => {
        let activeSections = []

        // Determine active sections based on props and query parameters
        if (expandAll) {
            activeSections = SECTIONS
        } else if (query.active) {
            activeSections = Array.isArray(query.active) ? query.active : query.active.split(',')
        }

        // Add sections with errors to the active sections
        if (expandErrors) {
            activeSections = [...activeSections, ...expandErrors]
        }

        // Handle the initial opening of sections and any sections that have errors.
        // If `initOpenSections` are provided and the component is not yet ready (`readyRef.current` is false), add these sections to `activeSections` to ensure they are open when the component renders.
        if (initOpenSections && !readyRef.current) {
            activeSections = [...activeSections, ...initOpenSections]
        }

        // Handle initial error sections if no active sections exist.
        // Add default sections if there are errors, or just the provided error sections if only `isShowPhoneNumber` has an error.
        if (initOpenErrorSections && !activeSections.length && !readyRef.current) {
            const hasOtherErrors = Object.keys(errors).some(key => key !== 'isShowPhoneNumber' && errors[key])
            const isShowPhoneNumberError = errors.isShowPhoneNumber

            if (isShowPhoneNumberError && !hasOtherErrors) {
                activeSections = [...activeSections, ...initOpenErrorSections]
            } else if (isShowPhoneNumberError || hasOtherErrors) {
                activeSections = [DEFAULT_ACTIVE_SECTIONS, ...activeSections, ...initOpenErrorSections]
            } else {
                // If no errors are present, keep the default sections
                activeSections = [...activeSections]
            }
        }

        // Default to active sections if no specific sections are active
        if (!activeSections.length && !readyRef.current) {
            activeSections = DEFAULT_ACTIVE_SECTIONS
        }

        // Mark component as initialized and update state
        readyRef.current = true

        setState({
            ready: true,
            activePanels: uniq(activeSections),
        })
    }, [setState, expandAll, expandErrors, query.active, initOpenSections]) // eslint-disable-line react-hooks/exhaustive-deps

    // Handle panel changes and update URL hash
    const handlePanelChange = (activeKey) => {
        setState({
            query: {}
        })

        const activeKeys = activeKey.length > 1 ? activeKey.join(',') : activeKey

        history.push({
            search: location.search,
            hash: `#active=${activeKeys}`
        })

        setState({
            query: { active: activeKeys }
        })
    }

    // Render collapsible sections if component is ready
    return !readyRef.current ? null : (
        <Collapse
            className={`accordion ${CLASS_NAME}`}
            accordion={false}
            activeKey={state.activePanels}
            onChange={(key) => handlePanelChange(key)}
        >
            {children}
        </Collapse>
    )
}

EditUserCollapse.propTypes = {
    children: PropTypes.node.isRequired,
    formSubmitCount: PropTypes.number.isRequired,
    formIsValid: PropTypes.bool.isRequired,
    expandAll: PropTypes.bool,
    initOpenSections: PropTypes.array,
}

export default EditUserCollapse
